/**
 * Unify Encounter FHIR model.
 *
 * An Encounter is created when a **billable** appointment is first created
 * (whether it enters as `proposed` or `booked`). It is the billing anchor for
 * each client session, linking the appointment to an EpisodeOfCare for
 * documentation and billing. Non-billable and intake appointments do NOT
 * create encounters.
 *
 * ┌──────────────────────────┬──────────────────────────────────────────────┐
 * │ FHIR field               │ Unify usage                                 │
 * ├──────────────────────────┼──────────────────────────────────────────────┤
 * │ status                   │ planned | finished | cancelled              │
 * │ class                    │ Always AMB (ambulatory)                     │
 * │ type                     │ Program-first: iop if IOP episode, else     │
 * │                          │ echoed from Appointment.serviceType         │
 * │ serviceType              │ Echoed from Appointment.serviceType         │
 * │ subject                  │ Reference<Patient> — billed client          │
 * │ appointment              │ Reference<Appointment>[] — triggering appt  │
 * │ episodeOfCare            │ Reference<EpisodeOfCare>[] — care context   │
 * │ participant              │ PPRF/SPRF Practitioners + PART RelatedPerson│
 * │ period                   │ Actual session start/end from Chime        │
 * │                          │ dual-presence (provider ∩ client), or      │
 * │                          │ scheduled window until attendance runs     │
 * │ length                   │ Actual session duration in minutes          │
 * ├──────────────────────────┼──────────────────────────────────────────────┤
 * │ EXTENSIONS               │                                             │
 * │ reason-cancelled         │ valueCodeableConcept — noshow, client-      │
 * │                          │ cancelled, client-late-cancelled, clinician- │
 * │                          │ cancelled                                   │
 * │ iop-parent-encounter     │ valueReference → Encounter (IOP parent)     │
 * └──────────────────────────┴──────────────────────────────────────────────┘
 *
 * Encounter creation rules:
 *   Individual         → 1 encounter (subject = client)
 *   Couples / Family   → 1 encounter (subject = identified patient)
 *                         + RelatedPerson participants carried over
 *   Group              → 1 encounter per client (subject = each client)
 *   Screening / Consult → 0 (intake, no encounter)
 *
 * Encounter.type derivation (program-first):
 *   1. If linked EpisodeOfCare.type is a program (iop) → type = iop
 *   2. Otherwise → type = Appointment.serviceType value
 */
import type {
  CodeableConcept,
  Encounter,
  EncounterParticipant,
  Reference,
} from '@medplum/fhirtypes';

import {
  CANCELLATION_REASON_SYSTEM,
  ENCOUNTER_REASON_CANCELLED_EXT,
  IOP_PARENT_ENCOUNTER_EXT,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal unions
// ═══════════════════════════════════════════════════════════════════════

/** Encounter statuses used by Unify (3-state model). */
export type EncounterStatus = 'planned' | 'finished' | 'cancelled';

/**
 * Cancellation reason codes (used in the `reason-cancelled` extension).
 * System: `https://unify.health/fhir/CodeSystem/cancellation-reason`
 */
export type EncounterCancellationReasonCode =
  | 'noshow'
  | 'client-cancelled'
  | 'client-late-cancelled'
  | 'clinician-cancelled'
  | 'assigned-in-error';

// ═══════════════════════════════════════════════════════════════════════
//  Standard field helpers
// ═══════════════════════════════════════════════════════════════════════

/** The billed patient (subject). */
export function getSubjectRef(enc: Encounter): Reference | undefined {
  return enc.subject;
}

/** Linked appointment(s). */
export function getAppointmentRefs(enc: Encounter): Reference[] {
  return enc.appointment ?? [];
}

/** Linked EpisodeOfCare(s). */
export function getEpisodeOfCareRefs(enc: Encounter): Reference[] {
  return enc.episodeOfCare ?? [];
}

/** All practitioner participants (PPRF + SPRF). */
export function getEncounterPractitioners(enc: Encounter): EncounterParticipant[] {
  return (
    enc.participant?.filter((p) =>
      p.individual?.reference?.startsWith('Practitioner/'),
    ) ?? []
  );
}

/** All RelatedPerson participants (PART — couples/family members). */
export function getEncounterRelatedPersons(enc: Encounter): EncounterParticipant[] {
  return (
    enc.participant?.filter((p) =>
      p.individual?.reference?.startsWith('RelatedPerson/'),
    ) ?? []
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Extension getters
// ═══════════════════════════════════════════════════════════════════════

/**
 * Cancellation reason when `status = cancelled`.
 * Returns the full CodeableConcept (system + code + display).
 */
export function getReasonCancelled(enc: Encounter): CodeableConcept | undefined {
  return enc.extension?.find(
    (e) => e.url === ENCOUNTER_REASON_CANCELLED_EXT,
  )?.valueCodeableConcept;
}

/** Cancellation reason code string (e.g. `"noshow"`). */
export function getReasonCancelledCode(enc: Encounter): EncounterCancellationReasonCode | undefined {
  return getReasonCancelled(enc)?.coding?.find(
    (c) => c.system === CANCELLATION_REASON_SYSTEM,
  )?.code as EncounterCancellationReasonCode | undefined;
}

/**
 * IOP parent encounter reference. Links IOP sub-sessions to a parent
 * encounter for per-diem billing aggregation.
 */
export function getIopParentEncounter(enc: Encounter): Reference | undefined {
  return enc.extension?.find((e) => e.url === IOP_PARENT_ENCOUNTER_EXT)?.valueReference;
}

// ═══════════════════════════════════════════════════════════════════════
//  Extension setters (mutate in place)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Set or remove the reason-cancelled extension.
 * Pass `undefined` to clear; pass a code to create the full CodeableConcept.
 */
export function setReasonCancelled(
  enc: Encounter,
  code: EncounterCancellationReasonCode | undefined,
): void {
  const others = (enc.extension ?? []).filter(
    (e) => e.url !== ENCOUNTER_REASON_CANCELLED_EXT,
  );
  if (!code) {
    enc.extension = others.length ? others : undefined;
    return;
  }
  const display: Record<EncounterCancellationReasonCode, string> = {
    noshow: 'No-Show',
    'client-cancelled': 'Client Cancelled',
    'client-late-cancelled': 'Client Late Cancelled',
    'clinician-cancelled': 'Clinician Cancelled',
    'assigned-in-error': 'Assigned in error',
  };
  enc.extension = [
    ...others,
    {
      url: ENCOUNTER_REASON_CANCELLED_EXT,
      valueCodeableConcept: {
        coding: [
          {
            system: CANCELLATION_REASON_SYSTEM,
            code,
            display: display[code],
          },
        ],
      },
    },
  ];
}

/** Set or remove the IOP parent encounter reference. */
export function setIopParentEncounter(enc: Encounter, ref: Reference | undefined): void {
  const others = (enc.extension ?? []).filter((e) => e.url !== IOP_PARENT_ENCOUNTER_EXT);
  enc.extension = ref
    ? [...others, { url: IOP_PARENT_ENCOUNTER_EXT, valueReference: ref }]
    : others.length ? others : undefined;
}
